/**
 * rowPaths — locate a Switch row (or one of its cells) RELATIVE to the
 * top-level field that owns the array.
 *
 * A list field is committed as a whole (`setFieldValue('regions', array)`), so
 * a row-level primitive only needs to know WHERE inside that array it sits:
 * `0` (first region), `0.destinations.2` (third destination of the first
 * region), `0.destinations.2.city` (one cell). That relative path is derived
 * from reactive-json's own store paths — the absolute path of `~` (the current
 * row) minus the absolute path of the field — so it is exact whatever the
 * nesting, with no regex guessing on the render path.
 *
 * `field` uses a dotted notation for nested lists: `regions.destinations`
 * = the `destinations` list of a `regions` row. Only the first segment (the
 * top-level field) is committed; the remaining segments name the nested lists
 * an `add` action must descend into from its row.
 */

const isIndex = (segment) => /^\d+$/.test(segment)

/** `regions.destinations` → `{ topField: 'regions', nested: ['destinations'] }`. */
export const splitField = (field) => {
  const segments = String(field ?? '').split('.').filter(Boolean)
  return { topField: segments[0] ?? '', nested: segments.slice(1) }
}

/** `absPath` expressed relative to `basePath`, or `null` when not underneath. */
export const relativeTo = (basePath, absPath) => {
  if (typeof basePath !== 'string' || typeof absPath !== 'string') return null
  if (absPath === basePath) return ''
  return absPath.startsWith(basePath + '.') ? absPath.slice(basePath.length + 1) : null
}

/** Number of array levels a row path descends (`0.destinations.2` → 2). */
export const rowDepth = (rowPath) =>
  rowPath == null ? 0 : rowPath.split('.').filter(isIndex).length

/**
 * Resolve the row/cell context of a primitive.
 *
 * @param {object} args
 * @param {string} args.field        dotted field (`regions` | `regions.destinations`)
 * @param {string} [args.column]     cell key inside the row
 * @param {(dataLocation: string) => string|undefined} args.resolvePath
 *   absolute store path of a reactive-json data location (`~`, `~~.x`…)
 * @param {string} args.dataBase     unit data base location (`~~.apiData.data.objectData.data` | `~~`)
 * @returns {{topField: string, nested: string[], rowPath: string|null, cellPath: string|null}}
 *   `rowPath` is null outside any row of the field.
 */
export const resolveRowContext = ({ field, column, resolvePath, dataBase }) => {
  const { topField, nested } = splitField(field)
  const base = resolvePath(`${dataBase}.${topField}`)
  const rowAbs = resolvePath('~')
  const rowPath = relativeTo(base, rowAbs)
  // `''` = `~` IS the field itself (not a row) — treat as "outside a row".
  const inRow = rowPath != null && rowPath !== ''
  const cellPath = column != null && inRow ? `${rowPath}.${column}` : null
  return { topField, nested, rowPath: inRow ? rowPath : null, cellPath }
}

/**
 * Turn a row context + an action into what the runtime commits against:
 * the relative path of the TARGET ARRAY and the index the action applies to.
 *
 *  - `add`    : appends to the array named by `field`. From a row of that
 *               array's parent (`field: regions.destinations` inside a region)
 *               it targets `<row>.destinations`; from a row of the array itself
 *               it inserts right after that row.
 *  - `insert` : inserts after the current row.
 *  - `remove` / `up` / `down` : act on the current row.
 *
 * Returns null when the action makes no sense where the primitive is rendered
 * (e.g. `remove` outside any row).
 *
 * @returns {{arrayPath: string, index: number|null}|null}
 */
export const resolveRowAction = ({ action, nested, rowPath }) => {
  const depth = rowDepth(rowPath)
  const targetLevel = nested.length + 1
  const parentOf = (p) => {
    const segments = p.split('.')
    segments.pop()
    return segments.join('.')
  }
  const indexOf = (p) => Number(p.split('.').pop())

  if (action === 'add') {
    if (depth === targetLevel) return { arrayPath: parentOf(rowPath), index: indexOf(rowPath) }
    if (depth === targetLevel - 1) {
      // A row at depth d belongs to the list at level d; the list one level
      // below it is `nested[d - 1]`: row `0` + [`destinations`] → `0.destinations`.
      const arrayPath = depth === 0 ? '' : `${rowPath}.${nested[depth - 1]}`
      return { arrayPath, index: null }
    }
    return null
  }
  if (depth !== targetLevel) return null
  return { arrayPath: parentOf(rowPath), index: indexOf(rowPath) }
}
